import {SVG} from '../../../../../constants/assetLists';
import React, {useMemo, useRef, useState} from 'react';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import AppMenu from '../../../../../components/common/AppMenu';
import AppButton from '../../../../../components/common/AppButton';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';
import useScreenSize from '../../../../../hooks/useScreenSize';
import {useAnnouncementTaskMutations} from '../../../../../hooks/useAnnouncementTaskMutations';
import {debounce, getDateInUTC, getReservationInfo} from '../../../../../utils';
import {useNavigate} from 'react-router';
import DatePicker, {registerLocale} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {he} from 'date-fns/locale';
import {format} from 'date-fns';
import {useReactToPrint} from 'react-to-print';
import moment from 'moment';

registerLocale('he', he);

const CalendarButton = React.forwardRef<
  HTMLButtonElement,
  {value?: string; onClick?: () => void; formattedDate?: string}
>(({formattedDate, value, onClick}, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    className="flex items-center gap-3">
    <p className="m-0 text-[18px] font-medium text-deepAqua">
      {formattedDate || value}
    </p>
    <img
      src={SVG.dropdownGrayIcon}
      className="w-[10px] h-[5px] object-contain"
    />
  </button>
));

const BASE_MENUITEMS = [
  {
    title: 'אירוע',
    value: 'add',
    icon: SVG.addGrayIcon,
  },
  {
    title: 'הזמנת שולחן',
    value: 'reservation',
    icon: SVG.tableReservationGrayIcon,
  },
  {
    title: 'תצוגה יומית',
    value: 'daily',
  },
  {
    title: 'תצוגת תכנון',
    value: 'scheduled',
  },
  {
    title: 'הזמנות שולחן להדפסת',
    value: 'printReservation',
    icon: SVG.printTableIcon,
  },
  {title: 'מחיקה', value: 'delete', icon: SVG.deleteGrayIcon, disabled: true},
];

function Header() {
  const {
    setShowCalendarScreen,
    calendarSelectedDate,
    setCalendarSelectedDate,
    calendarDisplayType,
    setCalendarDisplayType,
    setOpenAddEditAnnouncementDialog,
    selectedCalendarEvent,
    setSelectedCalendarEvent,
    apiAnnouncementsCalendarData,
  } = useOperationsContext();

  const screenSize = useScreenSize();
  const isLarge = screenSize == 'xl' || screenSize == '2xl';
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showPrintReservationDialog, setShowPrintReservationDialog] =
    useState(false);
  const reservationPrintRef = useRef(null);

  const {deleteAnnouncementMutation} = useAnnouncementTaskMutations();

  const reservationRows = useMemo(() => {
    const anns: any[] = apiAnnouncementsCalendarData?.data ?? [];
    return anns
      .filter((a: any) => a.announcementType === 'Reservation')
      .map((a: any) => {
        const reservationInfo = getReservationInfo(a);
        return {
          title: reservationInfo?.label || a.announcementTitle || "—",
          description: a.description || "—",
          startDate: a.scheduling?.startDate
            ? moment.utc(a.scheduling.startDate).format("DD/MM/YYYY")
            : "—",
          startTime: a.scheduling?.startTime || "—",
          endTime: a.scheduling?.endTime || "—",
        };
      })
      ?.sort((a: any, b: any) => a.startTime.localeCompare(b.startTime));
  }, [apiAnnouncementsCalendarData]);

  const handleReservationPrint = useReactToPrint({
    contentRef: reservationPrintRef,
    documentTitle: 'הזמנות שולחן',
  });

  const menuItems = useMemo(
    () =>
      BASE_MENUITEMS.map(item =>
        item.value === 'delete'
          ? {...item, disabled: !selectedCalendarEvent}
          : item,
      ),
    [selectedCalendarEvent],
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = debounce((type: string) => {
    setCalendarSelectedDate(prev => {
      if (type === 'today') return getDateInUTC(new Date());
      else if (type === 'prev')
        return getDateInUTC(new Date(prev.setDate(prev.getDate() - 1)));
      else return getDateInUTC(new Date(prev.setDate(prev.getDate() + 1)));
    });
  }, 300);

  const handleCloseClick = () => {
    setShowCalendarScreen(false);
  };

  const handleConfirmDelete = () => {
    if (!selectedCalendarEvent) return;
    const announcementId =
      selectedCalendarEvent.source?._id ?? selectedCalendarEvent.id;
    deleteAnnouncementMutation.mutate(announcementId);
    setSelectedCalendarEvent(null);
    setShowConfirmationDialog(false);
  };

  const handleMenuChange = (item: string) => {
    if (item == 'daily') setCalendarDisplayType('daily');
    else if (item == 'scheduled') setCalendarDisplayType('scheduled');
    else if (item == 'add') {
      navigate('/operations', {state: {showInCalendar: true}});
      setOpenAddEditAnnouncementDialog(true);
    } else if (item == 'reservation') {
      navigate('/operations', {
        state: {initialType: 'Reservation'},
      });
      setOpenAddEditAnnouncementDialog(true);
    } else if (item == 'printReservation') {
      setShowPrintReservationDialog(true);
      handleMenuClose();
    } else if (item == 'delete') {
      setShowConfirmationDialog(true);
    }
  };

  return (
    <div
      className={`${
        isLarge && 'rounded-tl-[32px] rounded-tr-[32px]'
      } bg-white p-3 shadow-md z-[1]`}>
      <div className="flex flex-col gap-3">
        <div className="w-full flex justify-between items-center gap-2">
          <p className="m-0 text-[24px] leading-6 font-light text-black">
            יומן
          </p>

          <div className="flex items-center gap-3">
            <button className="w-[18px] h-[18px] hover:opacity-40">
              <img
                src={SVG.expandScreenGrayIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </button>

            {isLarge && (
              <button
                className="w-[14px] h-[14px] hover:opacity-40"
                onClick={handleCloseClick}>
                <img
                  src={SVG.closeGrayIcon}
                  alt="icon"
                  className="w-full h-full object-contain"
                />
              </button>
            )}
          </div>
        </div>

        <DatePicker
          selected={calendarSelectedDate}
          onChange={date => {
            if (date) setCalendarSelectedDate(getDateInUTC(date));
          }}
          locale="he"
          dateFormat="EEEE, d 'ב'MMMM"
          showYearDropdown
          showMonthDropdown
          scrollableYearDropdown
          customInput={
            <CalendarButton
              formattedDate={format(calendarSelectedDate, "EEEE, d 'ב'MMMM", {
                locale: he,
              }).replace(/^יום\s*/, '')}
            />
          }
          popperPlacement="bottom-start"
          showPopperArrow={false}
          portalId="datepicker-portal"
        />

        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-5">
            <AppButton
              className="border-2"
              onClick={() => handleDateSelect('today')}>
              היום
            </AppButton>

            <div className="flex items-center gap-10 ">
              <button
                className="w-[12px] h-[12px] hover:opacity-40"
                onClick={() => handleDateSelect('prev')}>
                <img
                  src={SVG.greaterThanGrayIcon}
                  alt="add"
                  className="w-full h-full object-contain"
                />
              </button>

              <button
                className="w-[12px] h-[12px] hover:opacity-40"
                onClick={() => handleDateSelect('next')}>
                <img
                  src={SVG.lessThanGrayIcon}
                  alt="add"
                  className="w-full h-full object-contain"
                />
              </button>
            </div>
          </div>

          <div data-calendar-selection-keep="true">
            <AppMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onOpen={handleMenuOpen}
              selectedItem={calendarDisplayType}
              onItemClick={item => handleMenuChange(item)}
              items={menuItems}
              showDivider
              dividerIndex={4}
            />
          </div>
        </div>
      </div>

      <AppConfirmationDialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={handleConfirmDelete}
        icon={<img src={SVG.deleteRedIcon} className="w-[48px] h-[48px]" />}
        title="שימו לב"
        subTitle="למחוק את האירוע?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />

      <AppConfirmationDialog
        open={showPrintReservationDialog}
        onClose={() => setShowPrintReservationDialog(false)}
        onConfirm={() => {
          setShowPrintReservationDialog(false);
          handleReservationPrint();
        }}
        icon={
          <img
            src={SVG.printerAquaLgIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title="הדפסה"
        subTitle="שליחה להדפסה?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />

      {/* Hidden reservation print layout */}
      <div className="hidden">
        <div
          ref={reservationPrintRef}
          dir="rtl"
          className="p-6 font-rubik text-sm text-right">
          <h2 className="m-0 mb-1 text-xl">הזמנות שולחן</h2>
          <div className="mb-4 text-gray text-sm">
            {moment(calendarSelectedDate).format('DD/MM/YYYY')}
          </div>

          {reservationRows.length === 0 ? (
            <p className="text-center py-8" style={{color: '#9E9E9E'}}>
              אין הזמנות שולחן להצגה
            </p>
          ) : (
            <table
              className="w-full border-collapse text-xs"
              style={{tableLayout: 'fixed'}}>
              <thead>
                <tr>
                  {['כותרת', 'תיאור', 'תאריך', 'משעה', 'לשעה'].map((h, i) => (
                    <th
                      key={i}
                      className="text-right px-2 py-1.5 border-b-2 font-medium"
                      style={{
                        borderColor: '#E0E0E0',
                        color: '#757575',
                        width: i === 1 ? '35%' : undefined,
                      }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reservationRows.map((row: any, idx: number) => (
                  <tr key={idx}>
                    {[
                      row.title,
                      row.description,
                      row.startDate,
                      row.startTime,
                      row.endTime,
                    ].map((cell, ci) => (
                      <td
                        key={ci}
                        className="text-right px-2 py-1.5 border-b"
                        style={{
                          borderColor: '#F5F5F5',
                          wordBreak: 'break-word',
                        }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Header;
